import {
  Children,
  createContext,
  isValidElement,
  useContext,
  useId,
  useMemo,
  type ReactElement,
  type ReactNode,
} from 'react';
import './styles/field.css';

const FIELD_CONTROL_NAMES = new Set([
  'cevopInput',
  'cevopSelect',
  'cevopCurrencyInput',
  'cevopQuantityInput',
  'cevopPercentageInput',
  'cevopPasswordInput',
  'cevopPhoneInput',
  'cevopTextarea',
  'cevopCheckbox',
  'cevopToggle',
]);

export type FieldContextValue = {
  controlId: string;
  labelId: string;
  hintId?: string;
  errorId?: string;
  describedBy?: string;
  invalid: boolean;
  required: boolean;
};

const FieldContext = createContext<FieldContextValue | null>(null);

export function useField(): FieldContextValue | null {
  return useContext(FieldContext);
};

type FieldControlProps = {
  id?: string;
  invalid?: boolean;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'false' | 'grammar' | 'spelling' | 'true';
  'aria-required'?: boolean | 'false' | 'true';
};

/** Merges parent Field metadata into a form control without overriding explicit consumer props. */
export function useFieldControlProps<T extends FieldControlProps>(props: T): T & FieldControlProps {
  const field = useContext(FieldContext);
  if (!field) return props;

  const describedBy =
    [props['aria-describedby'], field.describedBy].filter(Boolean).join(' ') || undefined;
  const mergedInvalid = props.invalid ?? field.invalid;
  const mergedRequired = props.required ?? field.required;

  return {
    ...props,
    id: props.id ?? field.controlId,
    'aria-describedby': describedBy,
    'aria-invalid': mergedInvalid ? true : props['aria-invalid'],
    'aria-required': mergedRequired ? true : props['aria-required'],
    required: mergedRequired ? true : props.required,
  };
}

function isCheckboxControl(child: ReactNode): boolean {
  if (!isValidElement(child)) return false;
  const type = child.type;
  if (typeof type === 'string') {
    return type === 'input' && child.props.type === 'checkbox';
  }
  return (type as { displayName?: string }).displayName === 'cevopCheckbox';
}

function isFieldControl(child: ReactNode): boolean {
  if (!isValidElement(child)) return false;
  const type = child.type;
  if (typeof type === 'string') {
    return type === 'input' || type === 'select' || type === 'textarea';
  }
  const name = (type as { displayName?: string }).displayName;
  return Boolean(name && FIELD_CONTROL_NAMES.has(name));
}

function firstFieldChild(children: ReactNode): ReactElement {
  const nodes = Children.toArray(children);
  const control = nodes.find((node) => isFieldControl(node) || isCheckboxControl(node));
  if (isValidElement(control)) return control;
  const first = nodes.find((node) => isValidElement(node));
  if (isValidElement(first)) return first;
  throw new Error('Field requires a child element');
}

function controlIdFromChild(child: ReactNode, fallbackId: string): string {
  if (isValidElement(child) && typeof child.props.id === 'string' && child.props.id.length > 0) {
    return child.props.id;
  }
  return fallbackId;
}

function requiredFromChild(child: ReactNode): boolean {
  return isValidElement(child) && Boolean(child.props.required);
}

export type FieldProps = {
  label: string;
  hint?: string;
  error?: string;
  /** Marks the field as required for label copy and control semantics. Inferred from the child control when omitted. */
  required?: boolean;
  /** Explicit control id. Overridden when the child control supplies its own `id`. */
  id?: string;
  /**
   * `stacked` — label above control (default).
   * `inline` — checkbox beside label (for Checkbox children).
   */
  layout?: 'stacked' | 'inline';
  /** Visually hide the label while keeping it available to assistive tech. */
  hideLabel?: boolean;
  children: ReactNode;
};

export function Field({
  label,
  hint,
  error,
  required,
  id: explicitId,
  layout = 'stacked',
  hideLabel = false,
  children,
}: FieldProps) {
  const generated = useId();
  const baseId = explicitId ?? `field-${generated.replace(/:/g, '')}`;
  const child = firstFieldChild(children);
  const controlId = controlIdFromChild(child, baseId);
  const labelId = `${controlId}-label`;
  const hintId = hint ? `${controlId}-hint` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const describedBy = [errorId, error ? undefined : hintId].filter(Boolean).join(' ') || undefined;
  const isRequired = required ?? requiredFromChild(child);
  const invalid = Boolean(error);
  const associatesControl = isFieldControl(child);
  const inlineCheckbox = layout === 'inline' && isCheckboxControl(child);

  const contextValue = useMemo<FieldContextValue>(
    () => ({
      controlId,
      labelId,
      hintId,
      errorId,
      describedBy,
      invalid,
      required: isRequired,
    }),
    [controlId, labelId, hintId, errorId, describedBy, invalid, isRequired],
  );

  const requiredMark = isRequired ? (
    <>
      <span aria-hidden="true"> *</span>
      <span className="sr-only"> (required)</span>
    </>
  ) : null;

  return (
    <FieldContext.Provider value={contextValue}>
      <div
        className={['cev-field', inlineCheckbox && 'cev-field--inline'].filter(Boolean).join(' ')}
        role={associatesControl && !inlineCheckbox ? undefined : inlineCheckbox ? undefined : 'group'}
        aria-labelledby={associatesControl && !inlineCheckbox ? undefined : inlineCheckbox ? undefined : labelId}
      >
        {inlineCheckbox ? (
          <label htmlFor={controlId} id={labelId} className="cev-field__inline-control">
            {children}
            <span className="cev-field__inline-label">
              {label}
              {requiredMark}
            </span>
          </label>
        ) : associatesControl ? (
          <label htmlFor={controlId} id={labelId} className={hideLabel ? 'cev-field__label sr-only' : 'cev-field__label'}>
            {label}
            {requiredMark}
          </label>
        ) : (
          <div id={labelId} className={hideLabel ? 'cev-field__label sr-only' : 'cev-field__label'}>
            {label}
            {requiredMark}
          </div>
        )}
        {!inlineCheckbox ? children : null}
        {error ? (
          <span id={errorId} role="alert" className="cev-field__error">
            {error}
          </span>
        ) : hint ? (
          <span id={hintId} className="cev-field__hint">
            {hint}
          </span>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}
